import { useEffect, useState } from 'react';
import { Grid, Typography } from '@material-ui/core';
import { AnnouncementCard } from '../../../ui/card';
import { fetchApiRef, useApi } from '@backstage/core-plugin-api';
import { DateTime } from 'luxon';

export const ANNOUNCEMENT_COUNT = 6;
export const AUTO_HIDE_DAYS = 30;

type Announcement = {
  id: number;
  title: string;
  created: string;
  tags: string[];
};

export const isExpired = (announcement: Announcement): boolean => {
  if (!announcement.tags?.includes('auto-hide-30-days')) {
    return false;
  }
  const createdDate = DateTime.fromISO(announcement.created);
  const now = DateTime.now();
  return now.diff(createdDate, 'days').days >= AUTO_HIDE_DAYS;
};

const AsGridItem = ({ children }: { children: React.ReactNode }) => (
  <Grid item xs={12}>
    {children}
  </Grid>
);

export const AnnouncementList = () => {
  const fetchApi = useApi(fetchApiRef);

  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const baseUrl =
    process.env.NODE_ENV === 'production'
      ? '/api/qeta/collections/1'
      : 'http://localhost:7007/api/qeta/collections/1';

  useEffect(() => {
    const fetchAnnouncements = async () => {
      try {
        setLoading(true);
        const response = await fetchApi.fetch(baseUrl);
        if (!response.ok) {
          setError('Unable to load announcements.');
        }
        const announcementsCollection = await response.json();
        if (announcementsCollection?.posts) {
          const sortedAnnouncements = announcementsCollection.posts
            .filter(
              (announcement: Announcement) =>
                announcement &&
                !announcement.tags?.includes('archived-announcement') &&
                !isExpired(announcement),
            )
            .sort((a: { tags?: string[] }, b: { tags?: string[] }) => {
              const isPinnedA = a.tags?.includes('pinned') ? 1 : 0;
              const isPinnedB = b.tags?.includes('pinned') ? 1 : 0;
              return isPinnedB - isPinnedA;
            });
          setAnnouncements(sortedAnnouncements.slice(0, ANNOUNCEMENT_COUNT));
        }
      } catch (err) {
        setError(
          `Unable to load announcements: ${
            err instanceof Error ? err.message : String(err)
          }. Please try refreshing the page.`,
        );
      } finally {
        setLoading(false);
      }
    };
    fetchAnnouncements();
  }, [fetchApi, baseUrl]);

  return (
    <Grid container spacing={2}>
      {loading && (
        <AsGridItem>
          <Typography>Loading announcements...</Typography>
        </AsGridItem>
      )}
      {error && (
        <AsGridItem>
          <Typography>{error}</Typography>
        </AsGridItem>
      )}
      {announcements.length === 0 && (
        <AsGridItem>
          <Typography>No announcements to show</Typography>
        </AsGridItem>
      )}

      {announcements.length > 0 &&
        announcements.map(announcement => (
          <AsGridItem key={announcement.id}>
            <AnnouncementCard announcement={announcement} />
          </AsGridItem>
        ))}
    </Grid>
  );
};
